import * as React from 'react';
import { MessageBarType, Stack } from '@fluentui/react';
import { CmicContext } from '../../context/CmicContext';
import { THEME_ID, baseFontFamilyStyle } from '../../shared/styles';
import CustomTextInput from '../../components/common/CustomTextInput';
import CustomDropbox from '../../components/common/CustomDropbox';
import UploadHeader from './components/UploadHeader';
import { CMIC_TREE_NODE_TYPES } from '../../shared/Constants';
import { extractRequiredFields, gatherAttachmentFields, gatherRevisionsFields } from './services/utility';
import { FIELD_TYPES } from './services/constants';
import { cfLovs, documentStatus } from './services/services';
import { isInternetAvailable } from '../../shared/UtilityFunctions';

const UploadDocumentAttch = ({
	docData,
	itemFromEmail,
	requiredFields,
	activeNode,
	hideForm,
	onSubmitForm,
	parentClassName,
}) => {
	const { useEffect, useContext, useCallback, useState } = React;

	const { userInfo, currentOfficeTheme, setShowToast } = useContext(CmicContext);

	const [formFields, setFormFields] = useState({});
	const [isFormSubmitted, setIsFormSubmitted] = useState(false);

	//Styles
	const headerTextStyle = {
		fontWeight: '6000',
		fontFamily: baseFontFamilyStyle,
		color:
			currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
				? currentOfficeTheme.bodyForegroundColor
				: '#5E6367',
		margin: '0.6rem 3px',
	};

	const uploadIconContainerStyles = {
		border:
			currentOfficeTheme.themeId === THEME_ID.BLACK || currentOfficeTheme.themeId === THEME_ID.DARK_GREY
				? `0.8px solid ${currentOfficeTheme.bodyForegroundColor}`
				: '1px solid rgba(174, 180, 189, 1)',
	};

	const formContainerStyles = {
		display: 'flex-block',
		flex: 1,
		paddingBottom: '1rem',
		overflowY: 'scroll',
	};

	const parentStackStyles = {
		root: {
			height: '100dvh',
			background: 'transparent',
			alignContent: 'center',
			padding: '0 0.5rem 0.1rem 0.5rem',
			position: 'relative',
		},
	};

	const textIconStyles = name => ({
		iconName: name,
		styles: {
			root: {
				color: currentOfficeTheme.bodyForegroundColor,
			},
		},
	});

	useEffect(() => {
		if (activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION) {
			handleRevisionFields(requiredFields);
		} else {
			handleFields(requiredFields);
		}
	}, [requiredFields, activeNode]);

	useEffect(() => {}, [itemFromEmail]);
	//@desc Customized fields handling
	// Adding Status drop as a default field
	const handleFields = useCallback(fields => {
		const formattedFields = gatherAttachmentFields(fields, docData[activeNode]?.PmdgtAutoDocNumberFlag === 'N');

		setFormFields(formattedFields);
	}, []);

	const handleRevisionFields = useCallback(fields => {
		const formattedFields = gatherRevisionsFields(fields);
		setFormFields(formattedFields);
	}, []);

	const handleInputChange = (name, val) => {
		const fields = { ...formFields };

		if (fields[name]?.RequiredFlag === 'Y' && !val) {
			fields[name].isValid = false;
		} else if (fields[name]?.RequiredFlag === 'Y' && val) fields[name].isValid = true;

		fields[name].value = val;

		setFormFields({ ...fields });
	};

	const handleFormUpload = att => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		let fields = { ...formFields };
		let isFormInvalid = false;

		// Checking data field by field
		Object.keys(fields).forEach(key => {
			if (fields[key].RequiredFlag === 'Y' && !fields[key].value) {
				fields[key].isValid = false;
				isFormInvalid = true;
			}
		});

		// If any mandatory field remains empty we'll throw error
		if (isFormInvalid)
			setShowToast({
				isVisible: true,
				msg: 'Please fill mandatory fields',
				type: MessageBarType.error,
			});
		else {
			const extractedFields = extractRequiredFields(fields);
			onSubmitForm(extractedFields);
		}

		setFormFields(fields);
		setIsFormSubmitted(true);
	};

	const handleLoadOptions = async info => {
		if (info.FieldDesc === 'Status') {
			return await documentStatus(userInfo);
		} else {
			return await cfLovs(info, userInfo);
		}
	};

	const handleHeader = useCallback(() => {
		if (activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION) {
			return {
				mainHeading: 'Create Revision',
				subHeading: `${docData[CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE]?.name} / ${docData[activeNode]?.name} ( ${
					docData[activeNode]?.PmdId
				} )`,
			};
		} else if (activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE) {
			return {
				mainHeading: 'Create Document',
				subHeading: docData[activeNode]?.name,
			};
		} else {
			return {
				mainHeading: '',
				subHeading: '',
			};
		}
	}, [docData, activeNode]);

	const fieldsUI = Object.values(formFields).map(field => {
		if (field.type === FIELD_TYPES.DROPDOWN) {
			return (
				<CustomDropbox
					key={field.name}
					title={field.FieldDesc}
					fieldInfo={field}
					currentOfficeTheme={currentOfficeTheme}
					headerTextStyle={headerTextStyle}
					invalid={isFormSubmitted && !field.isValid}
					onLoadOptions={handleLoadOptions}
					onChange={handleInputChange}
				/>
			);
		} else {
			return (
				<CustomTextInput
					key={field.name}
					title={field.FieldDesc}
					currentOfficeTheme={currentOfficeTheme}
					headerTextStyle={headerTextStyle}
					fieldInfo={field}
					invalid={isFormSubmitted && !field.isValid}
					onChange={handleInputChange}
					iconProps={field?.iconName ? textIconStyles(field.iconName) : {}}
					disabled={field?.isDisabled}
				/>
			);
		}
	});

	return (
		<Stack className={parentClassName} styles={parentStackStyles}>
			<UploadHeader
				title={handleHeader()}
				onPressBack={hideForm}
				onPressUpload={handleFormUpload}
				headerTextStyle={{ color: currentOfficeTheme.bodyForegroundColor }}
				subHeaderTextStyle={{ color: currentOfficeTheme.bodyForegroundColor, opacity: 0.7, fontSize: 12 }}
				iconContainerStyles={uploadIconContainerStyles}
			/>
			<Stack className="createNewAction" style={formContainerStyles}>
				{fieldsUI}
			</Stack>
		</Stack>
	);
};

export default UploadDocumentAttch;
